import React, { useState, useEffect, useCallback } from "react";
import { match as Match } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useIntl } from "react-intl";
import { Location } from "history";

import ProductsTable from "./table";
import {
  EnumServiceType,
  SearchableProductsFilterInput,
  SearchableProductSortableFields,
  SearchableSortDirection,
} from "../../../../lib/ground-aws-graphql-core/api/graphql/types";
import { GroundGraphqlContextStore } from "../../../../lib/ground-aws-graphql-core";
import PageSubheader from "../../../../components/PageSubheader";
import {
  EnumPaths,
  getServicesPathByTypology,
  getSpecificIdFromUrl,
  navTo,
} from "../../../../utils/navigation";
import history from "../../../../history";
import {
  getServiceTypologiesFilter,
  getSpaceTypologiesFilter,
} from "../../../../utils/types";
import { DEFAULT_LIMIT } from "../../../../utils/config";
import { TableChangeParams } from "../../../../components/Table/types";

interface Props {
  serviceType: EnumServiceType;
  match: Match<{ cid: string; id: string }>;
  location: Location<{
    pageIndex: number;
    limit: number;
    type: string;
  }>;
}

const ListProducts = ({ match, serviceType, location }: Props): JSX.Element => {
  const intl = useIntl();
  const [loading, setLoading] = useState(false);
  const [initialParams, setInitialParams] = useState<TableChangeParams | null>(
    location?.state
  );

  const searchProducts = GroundGraphqlContextStore.useStoreActions(
    actions => actions.product.searchProducts
  );

  const searchWorkSpaces = GroundGraphqlContextStore.useStoreActions(
    actions => actions.product.searchWorkSpaces
  );

  const total = GroundGraphqlContextStore.useStoreState(
    state => state.product.products.total
  );

  const setProduct = GroundGraphqlContextStore.useStoreActions(
    actions => actions.product.setProduct
  );
  const { id, cid } = match.params;

  const filter: SearchableProductsFilterInput =
    serviceType === EnumServiceType.SERVICE && id
      ? {
          or: getServiceTypologiesFilter(),
          serviceId: { eq: id },
        }
      : {
          productCenterId: { eq: cid },
          or: getSpaceTypologiesFilter(),
        };

  const fetchData = useCallback(
    async (pageIndex = 0, showLoader = true, limit?: number) => {
      if (showLoader) setLoading(true);
      const l = limit || DEFAULT_LIMIT;
      const from = pageIndex * l;
      try {
        if (serviceType === EnumServiceType.SERVICE && id) {
          await searchProducts({
            buildingId: cid,
            limit: l,
            filter,
            from,
            sort: {
              field: SearchableProductSortableFields.position,
              direction: SearchableSortDirection.asc,
            },
          });
        } else {
          await searchWorkSpaces({
            limit: l,
            filter,
            from,
            sort: {
              field: SearchableProductSortableFields.position,
              direction: SearchableSortDirection.asc,
            },
          });
        }
      } finally {
        setLoading(false);
      }
    },
    [match.params, serviceType, searchProducts, searchWorkSpaces]
  );

  const handleAddNewProduct = useCallback(() => {
    const centerId = getSpecificIdFromUrl(
      history.location.pathname,
      "centers/"
    );
    if (!centerId) return;

    setProduct(null);

    let path = `${getServicesPathByTypology(serviceType)}/${
      EnumPaths.PRODUCTS
    }/${EnumPaths.CREATION_MODE}`;
    const serviceId = getSpecificIdFromUrl(
      history.location.pathname,
      "services/"
    );

    if (serviceType === EnumServiceType.SERVICE && serviceId) {
      path = `${getServicesPathByTypology(serviceType)}/${serviceId}/${
        EnumPaths.PRODUCTS
      }/${EnumPaths.CREATION_MODE}`;
    }

    history.push(navTo(path, centerId));
  }, [serviceType, setProduct]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleOnChange = (changeParams: TableChangeParams) => {
    setInitialParams(changeParams);
    fetchData(changeParams.pageIndex, true, changeParams.limit);
  };

  return (
    <>
      <Helmet>
        <title>
          {intl.formatMessage({
            id: `page.list.products.${serviceType}.document.title`,
          })}
        </title>
      </Helmet>

      <PageSubheader
        title={`page.list.${serviceType}.title`}
        nbOfResults={total}
        buttonRightTitle={`page.list.products.create.product.${serviceType}`}
        buttonRightAction={handleAddNewProduct}
        buttonRightId={`btn-add-product-${serviceType.toLowerCase()}`}
      />

      <div className="px-8">
        <ProductsTable
          serviceType={serviceType}
          serviceId={id}
          initialParams={initialParams}
          onChange={handleOnChange}
          loading={loading}
          setLoading={setLoading}
          match={match}
        />
      </div>
    </>
  );
};

export default ListProducts;
